/**
 * CrmObjectAvatar - Smart avatar component for CRM objects.
 *
 * Displays appropriate avatar based on object type:
 * - Primary: Profile photo URL (ahoy_profile_photo_url) or Avatar URL (ahoy_avatar_url)
 * - Secondary: Gravatar (contacts) or Favicon (companies)
 * - Fallback: Initials or icon based on type
 */

import { useEffect, useState } from 'react';

import { Avatar, type AvatarProps } from '@/lib/base/avatar/avatar';
import { IMAGE_AVATAR_TYPES } from '@/lib/crm/core/entityTypes';
import type { CrmObject } from '@/types/crm';
import { getPropertyValue } from '@/types/crm';
import { cx } from '@/utils/cx';

import {
  createFaviconLoadGuard,
  getAvatarInitials,
  getFaviconUrl,
  getGravatarUrl,
} from '../utils/avatar';
import { getDisplayName } from '../utils/getDisplayName';
import { getEntityTypeIconComponent } from '../utils/icons';
import { isAllowedUrl } from '../utils/urlValidation';

export interface CrmObjectAvatarProps {
  /** The CRM object to display avatar for */
  object: CrmObject;
  /** Avatar size (passed to Avatar component) */
  size?: AvatarProps['size'];
  /** Additional CSS classes */
  className?: string;
}

/** Size to pixel mapping for avatar images */
const SIZE_TO_PIXELS: Record<NonNullable<AvatarProps['size']>, number> = {
  xxs: 32,
  xs: 48,
  sm: 64,
  md: 80,
  lg: 96,
  xl: 112,
  '2xl': 128,
  '3xl': 176,
};

/**
 * Get a property value from object.properties.
 */
function getObjectProperty<T>(
  object: CrmObject,
  propertyName: string
): T | undefined {
  return getPropertyValue<T>(object.properties?.[propertyName]);
}

/**
 * Get the display name for initials generation.
 * Uses the shared getDisplayName utility, with type-specific fallbacks.
 */
function getAvatarDisplayName(object: CrmObject): string {
  // Try the standard display name from properties
  const displayName = getDisplayName(object, '');
  if (displayName) {
    return displayName;
  }

  // Type-specific fallbacks for avatar generation

  // For users, use display name
  if (object.entity_type_name === 'ahoy_user') {
    return (
      getObjectProperty<string>(object, 'ahoy_display_name') || 'Unknown user'
    );
  }

  // For contacts, combine first and last name
  if (object.entity_type_name === 'ahoy_contact') {
    const firstName =
      getObjectProperty<string>(object, 'ahoy_first_name') || '';
    const lastName = getObjectProperty<string>(object, 'ahoy_last_name') || '';
    const fullName = `${firstName} ${lastName}`.trim();
    return fullName || 'Unknown contact';
  }

  // For companies, use name
  if (object.entity_type_name === 'ahoy_company') {
    return getObjectProperty<string>(object, 'ahoy_name') || 'Unknown company';
  }

  // For deals, use name
  if (object.entity_type_name === 'ahoy_deal') {
    return getObjectProperty<string>(object, 'ahoy_name') || 'Unknown deal';
  }

  return 'Unknown';
}

/**
 * Available sizes from external avatar services (powers of 2).
 * Google favicon service supports up to 256, Gravatar supports up to 2048.
 * We use 256 as max since that covers our largest avatar size (176px for 3xl).
 */
const AVATAR_SIZES = [16, 32, 64, 128, 256] as const;

/**
 * Get the best size to request for a given pixel size.
 * Returns the smallest power of 2 that is >= the requested size, capped at 256.
 */
function getRequestSize(pixelSize: number): number {
  for (const size of AVATAR_SIZES) {
    if (size >= pixelSize) {
      return size;
    }
  }
  return 256;
}

/**
 * Get primary avatar URL from object properties.
 * Checks ahoy_profile_photo_url and ahoy_avatar_url in priority order.
 * Returns null if not set or invalid protocol.
 */
function getPrimaryAvatarUrl(object: CrmObject): string | null {
  // Priority 1: profile photo URL (from LinkedIn enrichment, etc.)
  const profilePhotoUrl = getObjectProperty<string>(
    object,
    'ahoy_profile_photo_url'
  );
  if (profilePhotoUrl && isAllowedUrl(profilePhotoUrl)) {
    return profilePhotoUrl;
  }

  // Priority 2: avatar URL (for users)
  const avatarUrl = getObjectProperty<string>(object, 'ahoy_avatar_url');
  if (avatarUrl && isAllowedUrl(avatarUrl)) {
    return avatarUrl;
  }

  return null;
}

/**
 * Get secondary avatar image URL (type-specific fallback).
 * Returns null if no appropriate source is available.
 */
function getSecondaryAvatarSrc(
  object: CrmObject,
  pixelSize: number
): string | null {
  const requestSize = getRequestSize(pixelSize);

  // For contacts, try Gravatar from email (or DiceBear for .harbour contacts)
  if (object.entity_type_name === 'ahoy_contact') {
    const emailValue = getObjectProperty<string | string[]>(
      object,
      'ahoy_email'
    );
    const email = Array.isArray(emailValue) ? emailValue[0] : emailValue;
    if (email) {
      if (email.endsWith('.harbour')) {
        const displayName = getAvatarDisplayName(object);
        return `https://api.dicebear.com/9.x/notionists/svg?seed=${encodeURIComponent(displayName)}`;
      }
      return getGravatarUrl(email, requestSize);
    }
    return null;
  }

  // For companies, try favicon from domain
  if (object.entity_type_name === 'ahoy_company') {
    const domainValue = getObjectProperty<string | string[]>(
      object,
      'ahoy_domain'
    );
    const domain = Array.isArray(domainValue) ? domainValue[0] : domainValue;
    if (domain) {
      return getFaviconUrl(domain, requestSize);
    }
    return null;
  }

  // Users and deals don't have type-specific secondary sources
  return null;
}

/** Size classes for favicon/icon container */
const AVATAR_SIZE_CLASSES: Record<NonNullable<AvatarProps['size']>, string> = {
  xxs: 'size-4',
  xs: 'size-6',
  sm: 'size-8',
  md: 'size-10',
  lg: 'size-12',
  xl: 'size-14',
  '2xl': 'size-16',
  '3xl': 'size-[88px]',
};

/** Icon size classes for deal avatar */
const ICON_SIZE_CLASSES: Record<NonNullable<AvatarProps['size']>, string> = {
  xxs: 'size-2.5',
  xs: 'size-3.5',
  sm: 'size-5',
  md: 'size-6',
  lg: 'size-7',
  xl: 'size-8',
  '2xl': 'size-9',
  '3xl': 'size-12',
};

/**
 * FaviconAvatar - Square avatar with transparent background for company favicons.
 */
function FaviconAvatar({
  src,
  alt,
  size,
  fallback,
  className,
}: {
  src: string;
  alt: string;
  size: NonNullable<AvatarProps['size']>;
  fallback: React.ReactNode;
  className?: string;
}): React.ReactNode {
  const [isFailed, setIsFailed] = useState(false);

  if (isFailed) {
    return fallback;
  }

  return (
    <div
      data-avatar
      className={cx(
        'relative flex shrink-0 items-center justify-center overflow-hidden rounded-md',
        AVATAR_SIZE_CLASSES[size],
        className
      )}
    >
      <img
        src={src}
        alt={alt}
        className='block size-full object-contain'
        onLoad={
          src.includes('/s2/favicons?')
            ? createFaviconLoadGuard(() => setIsFailed(true))
            : undefined
        }
        onError={() => setIsFailed(true)}
      />
    </div>
  );
}

/**
 * IconAvatar - Icon-based avatar for CRM objects that use icons instead of images.
 */
function IconAvatar({
  entityTypeName,
  size,
  className,
}: {
  entityTypeName: string;
  size: NonNullable<AvatarProps['size']>;
  className?: string;
}): React.ReactElement {
  const Icon = getEntityTypeIconComponent(entityTypeName);
  return (
    <span
      data-avatar
      className={cx(
        'relative inline-flex shrink-0 items-center justify-center rounded-full bg-secondary text-tertiary',
        AVATAR_SIZE_CLASSES[size],
        className
      )}
    >
      <Icon className={ICON_SIZE_CLASSES[size]} />
    </span>
  );
}

/**
 * ImageAvatar - Wrapper around Avatar with cascade fallback support.
 * Tries primary src first, falls back to secondary src on error, then initials.
 */
function ImageAvatar({
  primarySrc,
  secondarySrc,
  alt,
  initials,
  size,
  className,
}: {
  primarySrc: string | null;
  secondarySrc: string | null;
  alt: string;
  initials: string;
  size: NonNullable<AvatarProps['size']>;
  className?: string;
}): React.ReactElement {
  const [primaryFailed, setPrimaryFailed] = useState(false);

  // Reset failure state when primary src changes so we retry rendering new images
  useEffect(() => {
    setPrimaryFailed(false);
  }, [primarySrc]);

  // Determine which src to use: primary → secondary → null (for initials)
  const src = primarySrc && !primaryFailed ? primarySrc : secondarySrc;

  // Wrap Avatar to intercept image load errors when showing primary src
  if (src === primarySrc && primarySrc) {
    return (
      <div
        data-avatar
        className={cx(
          'relative flex shrink-0 items-center justify-center rounded-full bg-avatar-bg outline outline-transparent outline-avatar-contrast-border',
          AVATAR_SIZE_CLASSES[size],
          className
        )}
      >
        <img
          src={primarySrc}
          alt={alt}
          className='block size-full rounded-full object-cover'
          onError={() => setPrimaryFailed(true)}
        />
      </div>
    );
  }

  // Use base Avatar for secondary src or initials
  return (
    <Avatar
      size={size}
      src={src}
      alt={alt}
      initials={initials}
      className={className}
    />
  );
}

/**
 * CrmObjectAvatar component.
 *
 * Renders a smart avatar for CRM objects with automatic source detection:
 * - Primary: ahoy_profile_photo_url or ahoy_avatar_url (any object type)
 * - Secondary: Type-specific sources (Gravatar for contacts, favicon for companies)
 * - Fallback: Initials or icon based on type
 */
export function CrmObjectAvatar({
  object,
  size = 'md',
  className,
}: CrmObjectAvatarProps): React.ReactElement | null {
  // Types without image-based avatars get an icon
  if (!IMAGE_AVATAR_TYPES.has(object.entity_type_name)) {
    return (
      <IconAvatar
        entityTypeName={object.entity_type_name}
        size={size}
        className={className}
      />
    );
  }

  const pixelSize = SIZE_TO_PIXELS[size];
  const displayName = getAvatarDisplayName(object);
  const initials = getAvatarInitials(displayName);

  // Get avatar sources with priority: primary (profile/avatar URL) → secondary (type-specific)
  const primaryAvatarUrl = getPrimaryAvatarUrl(object);
  const secondaryAvatarSrc = getSecondaryAvatarSrc(object, pixelSize);

  // For companies with a domain, render favicon as square with transparent bg
  if (object.entity_type_name === 'ahoy_company') {
    const domainValue = getObjectProperty<string | string[]>(
      object,
      'ahoy_domain'
    );
    const domain = Array.isArray(domainValue) ? domainValue[0] : domainValue;
    if (domain) {
      const faviconSrc = domain.endsWith('.harbour')
        ? `https://api.dicebear.com/9.x/shapes/svg?seed=${encodeURIComponent(domain)}`
        : getFaviconUrl(domain, getRequestSize(pixelSize));

      // If primary avatar URL is set, use it with favicon as fallback
      // Otherwise, use favicon as primary
      const primarySrc = primaryAvatarUrl || faviconSrc;
      const fallbackSrc = primaryAvatarUrl ? faviconSrc : null;

      return (
        <FaviconAvatar
          src={primarySrc}
          alt={displayName}
          size={size}
          fallback={
            fallbackSrc ? (
              <FaviconAvatar
                src={fallbackSrc}
                alt={displayName}
                size={size}
                fallback={
                  <Avatar
                    size={size}
                    alt={displayName}
                    initials={initials}
                    className={className}
                  />
                }
                className={className}
              />
            ) : (
              <Avatar
                size={size}
                alt={displayName}
                initials={initials}
                className={className}
              />
            )
          }
          className={className}
        />
      );
    }
  }

  // For users and contacts, use round avatar with cascade fallback
  // Priority: primary avatar URL (profile/avatar) → secondary (Gravatar) → initials
  return (
    <ImageAvatar
      primarySrc={primaryAvatarUrl}
      secondarySrc={secondaryAvatarSrc}
      alt={displayName}
      initials={initials}
      size={size}
      className={className}
    />
  );
}
